/* eslint-disable */
import React, { useState, useMemo, useEffect } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  Paper,
  Grid,
  FormControl,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Stack,
  Box,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import styles from "./Login.module.scss";
import { loginSignIn, sendCode, verifyCode } from "../../actions/login";
import { connect } from "react-redux";
import MobileLogin from "./MobileLogin";
import { Headers } from "../../utils/Headers";
import { API_URL } from "../../config/settings";
import { User } from "../../utils/Authenticate";
import CountryCodeSelect from "../../components/Inputs/CountryCodeSelect/CountryCodeSelect";
import LanguageSwitch from "../../components/Inputs/LanguageSwitch/LanguageSwitch";
import {
  DEFAULT_DIAL_CODE,
  getCountryByDialCode,
  phoneNumberTest,
} from "../../config/countries";

// Built per-render so messages follow the active language. Yup.string() takes no
// message argument — the strings previously passed there never rendered.
const buildValidationSchema = (t) =>
  Yup.object({
    countryCode: Yup.string().required(t("validation:required")),
    phoneNumber: Yup.string()
      .required(t("validation:required"))
      .test({ ...phoneNumberTest, message: t("validation:invalidPhone") }),
    code: Yup.string()
      .matches(/^[0-9]{4}$/, t("validation:codeLength"))
      .when("$showCode", {
        is: true,
        then: Yup.string().required(t("validation:required")),
      }),
  });

function Login(props) {
  const { t, i18n } = useTranslation();
  const [showCode, setShowCode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [codeError, setCodeError] = useState(false);
  const [codeSuccess, setCodeSuccess] = useState(false);

  const validationSchema = useMemo(
    () => buildValidationSchema(t),
    [t, i18n.language]
  );

  const formik = useFormik({
    initialValues: {
      countryCode: DEFAULT_DIAL_CODE,
      phoneNumber: "",
      code: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!showCode) {
        setIsLoading(true);
        props.sendCode({
          countryCode: values.countryCode,
          phoneNumber: values.phoneNumber,
          onSuccess: (response) => {
            setIsLoading(false);
            if (response.success) {
              setShowCode(true);
            } else {
              // Show error message for invalid phone number
              formik.setFieldError(
                "phoneNumber",
                response.message || t("validation:invalidPhone")
              );
            }
          },
        });
      } else {
        // Verify code only if it's 4 digits
        if (values.code.length !== 4) return;

        setIsLoading(true);
        props.verifyCode({
          countryCode: values.countryCode,
          phoneNumber: values.phoneNumber,
          code: values.code,
          onSuccess: (response) => {
            setIsLoading(false);
            if (!response.success) {
              formik.setFieldError("code", response.message || t("validation:invalidCode"));
              setCodeError(true);
              setCodeSuccess(false);

              // Reset error state after animation
              setTimeout(() => {
                setCodeError(false);
              }, 1000);
            } else {
              setCodeSuccess(true);
              setCodeError(false);
            }
          },
        });
      }
    },
  });

  // Formik keeps the error object produced by the previous schema, so a language
  // change leaves an already-visible message in the old language. Re-run validation
  // only when something is already invalid — otherwise this would flag a pristine form.
  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) formik.validateForm();
  }, [i18n.language]);

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // Style for phone input - remove box-shadow on focus
  const phoneInputStyles = {
    "& .MuiOutlinedInput-root": {
      height: "48px",
      // MUI's default 14px would push the country-code adornment off the edge.
      paddingLeft: 0,
      "& fieldset": {
        borderColor: "#E3E5E8",
      },
      "&:hover fieldset": {
        borderColor: "#B0B4B9",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#E3E5E8",
        boxShadow: "none",
      },
      "&.Mui-error fieldset": {
        borderColor: "#FF4D4F",
      },
    },
  };

  // Add keyframe animations
  const keyframes = `
    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-5px); }
      75% { transform: translateX(5px); }
    }
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }
  `;

  // Style for code input - green borders when valid
  const codeInputStyles = {
    width: "64px",
    height: "64px",
    margin: "0 6px",
    "& .MuiInputBase-root": {
      height: "100%",
      width: "100%",
      borderRadius: "16px",
      backgroundColor: "#F8F9FA",
      border: "none",
      fontSize: "24px",
      fontWeight: 600,
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: "#F0F1F2",
      },
      "&.Mui-focused": {
        backgroundColor: "#FFFFFF",
        boxShadow: "0 0 0 2px #15DD95",
      },
      "&.Mui-error": {
        boxShadow: "0 0 0 2px #FF4D4F",
        animation: "shake 0.5s ease-in-out",
      },
      "&.valid": {
        boxShadow: "0 0 0 2px #15DD95",
        animation: "pulse 0.5s ease-in-out",
      },
    },
    "& .MuiInputBase-input": {
      padding: 0,
      textAlign: "center",
      caretColor: "#15DD95",
    },
  };

  const buttonStyles = {
    height: "48px",
    borderRadius: "12px",
    backgroundColor:
      formik.isValid && (formik.values.phoneNumber || formik.values.code)
        ? "#0CC1CE"
        : "#95E1E7",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "none",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor:
        formik.isValid && (formik.values.phoneNumber || formik.values.code)
          ? "#0BB1BD"
          : "#95E1E7",
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: "#95E1E7",
      color: "#FFFFFF",
    },
  };

  return (
    <div className={styles["login-container"]}>
      <style>{keyframes}</style>
      <Paper
        className={styles["login"]}
        elevation={3}
        sx={{ position: "relative" }}
      >
        {/* Pinned to the card corner. It cannot live in the form flow: the form
            has 50px of padding and is vertically centred, which left the switch
            floating mid-card on narrow screens. One switch serves both layouts. */}
        <LanguageSwitch
          sx={{ position: "absolute", top: "20px", right: "20px", zIndex: 2 }}
        />
        {window.innerWidth < 900 ? (
          <MobileLogin
            formik={formik}
            showCode={showCode}
            setShowCode={setShowCode}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        ) : (
          <Grid container spacing={2}>
            <Grid
              item
              xs={7}
              justifyContent={"space-around"}
              alignItems={"center"}
            >
              <img
                src="/login-assets/login-left-picture.png"
                alt={t("app:altIllustration")}
              />
            </Grid>
            <Grid style={{ height: "100%" }} item xs={5}>
              <Paper elevation={2}>
                <form onSubmit={formik.handleSubmit}>
                  <Stack
                    className={styles["login-form"]}
                    direction={"column"}
                    spacing={3}
                  >
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                      <img
                        style={{
                          width: "180px",
                          marginBottom: "24px",
                        }}
                        src="/login-assets/Logo-Log-In.svg"
                        alt="Logo"
                      />
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "24px",
                          fontWeight: 600,
                          color: "#1A1D1F",
                        }}
                      >
                        {t("auth:greeting")}
                      </h3>
                    </Box>

                    <FormControl>
                      <Box sx={{ mb: 1 }}>
                        <label
                          style={{
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: "#6F767E",
                          }}
                        >
                          {t("auth:phoneNumber")}
                        </label>
                      </Box>
                      <TextField
                        fullWidth
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formik.values.phoneNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.phoneNumber &&
                          Boolean(formik.errors.phoneNumber)
                        }
                        helperText={
                          formik.touched.phoneNumber &&
                          formik.errors.phoneNumber
                        }
                        placeholder={
                          getCountryByDialCode(formik.values.countryCode)
                            .placeholder
                        }
                        sx={phoneInputStyles}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CountryCodeSelect
                                value={formik.values.countryCode}
                                onChange={(e) => {
                                  formik.setFieldValue(
                                    "countryCode",
                                    e.target.value
                                  );
                                  // A number valid for one country is not valid
                                  // for another — clear it rather than submit it
                                  // under the new dial code.
                                  formik.setFieldValue("phoneNumber", "");
                                  formik.setFieldTouched("phoneNumber", false);
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>

                    {showCode && (
                      <>
                        <Box>
                          <Box sx={{ mb: 2 }}>
                            <label
                              style={{
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                color: "#6F767E",
                              }}
                            >
                              {t("auth:verificationCode")}
                            </label>
                          </Box>
                          <Stack direction="row" justifyContent="center">
                            {[0, 1, 2, 3].map((index) => (
                              <TextField
                                key={index}
                                name={`code${index}`}
                                value={formik.values.code[index] || ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d*$/.test(value)) {
                                    const newCode = [
                                      ...(formik.values.code || "").split(""),
                                    ];
                                    newCode[index] = value;
                                    const fullCode = newCode.join("");

                                    // Clear previous errors when typing
                                    if (codeError) {
                                      setCodeError(false);
                                      formik.setErrors({});
                                    }

                                    // Set the value in formik
                                    formik.setFieldValue("code", fullCode);

                                    // Move to next input
                                    if (value && index < 3) {
                                      const nextInput = document.querySelector(
                                        `input[name="code${index + 1}"]`
                                      );
                                      if (nextInput) nextInput.focus();
                                    }
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (
                                    e.key === "Backspace" &&
                                    !formik.values.code[index] &&
                                    index > 0
                                  ) {
                                    const prevInput = document.querySelector(
                                      `input[name="code${index - 1}"]`
                                    );
                                    if (prevInput) prevInput.focus();
                                  }
                                }}
                                error={codeError}
                                sx={codeInputStyles}
                                inputProps={{
                                  maxLength: 1,
                                  inputMode: "numeric",
                                  pattern: "[0-9]*",
                                }}
                                className={
                                  codeSuccess
                                    ? "valid"
                                    : codeError
                                    ? "error"
                                    : ""
                                }
                              />
                            ))}
                          </Stack>
                          {formik.touched.code && formik.errors.code && (
                            <Box
                              sx={{
                                textAlign: "center",
                                mt: 1,
                                color: "#FF4D4F",
                                fontSize: "0.875rem",
                              }}
                            >
                              {formik.errors.code}
                            </Box>
                          )}
                        </Box>

                        <Button
                          type="submit"
                          variant="contained"
                          sx={buttonStyles}
                          disabled={
                            formik.values.code.length !== 4 || isLoading
                          }
                        >
                          {isLoading ? t("auth:verifying") : t("auth:login")}
                        </Button>
                      </>
                    )}

                    {!showCode && (
                      <Button
                        type="submit"
                        variant="contained"
                        sx={buttonStyles}
                        disabled={
                          !formik.values.phoneNumber ||
                          Boolean(formik.errors.phoneNumber) ||
                          isLoading
                        }
                      >
                        {isLoading ? t("auth:sending") : t("auth:sendCode")}
                      </Button>
                    )}
                  </Stack>
                </form>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Paper>
    </div>
  );
}

const mapStateToProps = (state) => ({
  login: state.login,
});

const mapDispatchToProps = (dispatch) => ({
  loginSignIn: (data) => {
    dispatch(loginSignIn(data));
  },
  sendCode: (payload) => {
    dispatch(
      sendCode({
        countryCode: payload.countryCode,
        phoneNumber: payload.phoneNumber,
        onSuccess: payload.onSuccess,
      })
    );
  },
  verifyCode: (payload) => {
    dispatch(
      verifyCode({
        countryCode: payload.countryCode,
        phoneNumber: payload.phoneNumber,
        code: payload.code,
        onSuccess: payload.onSuccess,
      })
    );
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
